import { useState, useEffect, useCallback, useRef } from "react";
import { Message, AuthConfig, LoadMessagesResponse } from "../types";
import { loadMessages as apiLoadMessages } from "../utils/api";

export interface UseMessagesOptions {
  loadMessagesUrl: string;
  sessionId: string;
  authConfig?: AuthConfig;
  refetchInterval?: number;
  enablePolling?: boolean;
  onMessagesLoaded?: (messages: Message[]) => void;
  onError?: (error: Error) => void;
  onBlockStateChange?: (isBlocked: boolean) => void;
}

/**
 * Hook para gerenciar carregamento e polling de mensagens
 */
export const useMessages = ({
  loadMessagesUrl,
  sessionId,
  authConfig,
  refetchInterval = 3000,
  enablePolling = true,
  onMessagesLoaded,
  onError,
  onBlockStateChange,
}: UseMessagesOptions) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pollingResetKey, setPollingResetKey] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  // Usa refs para callbacks e authConfig para evitar loop infinito
  const onMessagesLoadedRef = useRef(onMessagesLoaded);
  const onErrorRef = useRef(onError);
  const onBlockStateChangeRef = useRef(onBlockStateChange);
  const authConfigRef = useRef(authConfig);
  const loadMessagesRef = useRef<() => Promise<void>>();

  // Atualiza refs quando callbacks mudam
  useEffect(() => {
    onMessagesLoadedRef.current = onMessagesLoaded;
    onErrorRef.current = onError;
    onBlockStateChangeRef.current = onBlockStateChange;
    authConfigRef.current = authConfig;
  });

  /**
   * Carrega mensagens do backend
   */
  const loadMessages = useCallback(async () => {
    if (!sessionId || !loadMessagesUrl) {
      console.log("[useMessages] Abortando: sessionId ou URL faltando");
      return;
    }

    console.log("[useMessages] Iniciando carregamento...");
    setIsLoading(true);
    setError(null);

    try {
      console.log("[useMessages] Chamando API...");
      const response: LoadMessagesResponse = await apiLoadMessages(
        loadMessagesUrl,
        sessionId,
        authConfigRef.current
      );

      console.log(
        "[useMessages] API retornou, isMounted:",
        isMountedRef.current
      );
      if (!isMountedRef.current) {
        console.log("[useMessages] Componente desmontado, abortando");
        return;
      }

      console.log("[useMessages] Resposta recebida:", {
        totalMensagens: response.messages.length,
        bloqueado: response.blockNewMessages,
        ultimasMensagens: response.messages
          .slice(-2)
          .map((m) => `${m.sender}: ${m.message}`),
      });

      const processedMessages = response.messages;

      console.log(
        "[useMessages] Setando",
        processedMessages.length,
        "mensagens no estado"
      );

      // Deduplica mensagens temporárias baseado em IDs
      setMessages((prevMessages) => {
        // Cria um Set de IDs das mensagens reais do backend
        const loadedIds = new Set(processedMessages.map(m => m.id));

        // Remove mensagens temporárias que já existem no backend (match por ID)
        const filteredPrevMessages = prevMessages.filter((msg) => {
          const isDuplicate = loadedIds.has(msg.id);

          if (isDuplicate) {
            console.log('[useMessages] Removendo mensagem temporária duplicada:', msg.id);
          }

          return !isDuplicate;
        });

        // Combina mensagens filtradas (temporárias não duplicadas) + novas do backend
        const mergedMessages = [...filteredPrevMessages, ...processedMessages];

        // Ordena por timestamp para garantir ordem cronológica
        mergedMessages.sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        // Verifica se houve mudanças
        if (prevMessages.length !== mergedMessages.length) {
          return mergedMessages;
        }

        const hasChanges = mergedMessages.some((msg, index) => {
          return (
            msg.id !== prevMessages[index]?.id ||
            msg.message !== prevMessages[index]?.message
          );
        });

        return hasChanges ? mergedMessages : prevMessages;
      });

      // Atualiza estado de bloqueio usando setState funcional
      setIsBlocked((prevBlocked) => {
        if (response.blockNewMessages !== prevBlocked) {
          onBlockStateChangeRef.current?.(response.blockNewMessages);
          return response.blockNewMessages;
        }
        return prevBlocked;
      });

      onMessagesLoadedRef.current?.(processedMessages);
    } catch (err) {
      console.log("[useMessages] ERRO capturado:", err);
      if (!isMountedRef.current) {
        console.log("[useMessages] Componente desmontado no erro, ignorando");
        return;
      }

      const error =
        err instanceof Error ? err : new Error("Erro ao carregar mensagens");
      console.log("[useMessages] Setando erro:", error.message);
      setError(error);
      onErrorRef.current?.(error);
    } finally {
      console.log("[useMessages] Finally - isMounted:", isMountedRef.current);
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    loadMessagesUrl,
    sessionId,
  ]);

  // Atualiza loadMessagesRef para uso no polling (após declaração)
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  /**
   * Adiciona uma mensagem localmente (otimista)
   */
  const addMessage = useCallback((message: Message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  /**
   * Atualiza o estado de bloqueio manualmente
   */
  const setBlockState = useCallback(
    (blocked: boolean) => {
      console.log("[useMessages] setBlockState chamado:", blocked);
      setIsBlocked(blocked);
      onBlockStateChangeRef.current?.(blocked);
    },
    []
  );

  /**
   * Atualiza uma mensagem específica
   */
  const updateMessage = useCallback(
    (messageId: string, updates: Partial<Message>) => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, ...updates } : msg))
      );
    },
    []
  );

  /**
   * Remove uma mensagem
   */
  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  /**
   * Inicia polling com intervalo customizado
   */
  const startPolling = useCallback(
    (interval?: number) => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      const pollInterval = interval || refetchInterval;
      intervalRef.current = setInterval(() => {
        loadMessagesRef.current?.();
      }, pollInterval);
    },
    [refetchInterval]
  );

  /**
   * Para polling
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Reseta o polling (reinicia o contador do intervalo)
   */
  const resetPolling = useCallback(() => {
    console.log('[useMessages] Resetando polling...');
    setPollingResetKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (sessionId) {
      setMessages([]);
      loadMessages();
    }
  }, [sessionId]);

  // Gerencia polling com intervalo fixo
  useEffect(() => {
    if (!enablePolling || !sessionId) {
      return;
    }

    console.log(`[useMessages] Iniciando polling: ${refetchInterval}ms (resetKey: ${pollingResetKey})`);

    // Limpa interval anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Cria novo interval com intervalo fixo
    intervalRef.current = setInterval(() => {
      loadMessagesRef.current?.();
    }, refetchInterval);

    // Cleanup: limpa interval quando dependências mudam ou componente desmonta
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enablePolling, sessionId, refetchInterval, pollingResetKey]);

  // Cleanup - apenas quando componente realmente desmonta
  useEffect(() => {
    isMountedRef.current = true;
    console.log("[useMessages] Componente montado, isMountedRef = true");

    return () => {
      console.log("[useMessages] Componente desmontando, isMountedRef = false");
      isMountedRef.current = false;
      stopPolling();
    };
  }, []); // Array vazio = executa apenas no mount/unmount

  return {
    messages,
    isLoading,
    isBlocked,
    error,
    loadMessages,
    addMessage,
    updateMessage,
    removeMessage,
    setBlockState, // Função para atualizar estado de bloqueio manualmente
    startPolling,
    stopPolling,
    resetPolling, // Função para resetar o polling (reinicia o contador)
  };
};
